import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { z } from "zod";

import { projectPersonaAwarenessSchema } from "./projectContext";

// Offer vocabulary as Zod enums so a drifting reading fails to parse instead of inventing a category.

/** Bump when a list or the reading shape changes; the sweep re-reads every row behind it. */
export const SIGHTGLASS_OFFER_READING_VERSION = 4;

/** Tags below this confidence stay out of filters, rankings columns and email. */
export const SIGHTGLASS_TAG_MIN_CONFIDENCE = 0.6;

/** Readings below this confidence never become offers. */
export const SIGHTGLASS_OFFER_MIN_CONFIDENCE = 0.5;

/** Sightings of one slot further apart than this start a new campaign. */
export const SIGHTGLASS_CAMPAIGN_GAP_DAYS = 10;

/** A page last captured longer ago than this is captured again when new ads point at it. */
export const SIGHTGLASS_RECAPTURE_AFTER_DAYS = 14;

export const sightglassOfferMechanicSchema = z
  .enum([
    "percent_off",
    "money_off",
    "buy_m_get_n",
    "tiered_volume",
    "bundle_price",
    "sitewide_sale",
    "free_gift",
    "free_shipping",
    "giveaway",
    "subscription",
    "trial_or_sample",
    "financing",
  ])
  .describe("SightglassOfferMechanic");
export type SightglassOfferMechanic = z.infer<
  typeof sightglassOfferMechanicSchema
>;

export const sightglassOfferTypeSchema = z
  .enum([
    "discount",
    "sale",
    "bundle",
    "free_gift",
    "free_shipping",
    "giveaway",
    "subscription",
    "trial",
    "financing",
  ])
  .describe(
    "SightglassOfferType: the mechanic grouped for display and filters",
  );
export type SightglassOfferType = z.infer<typeof sightglassOfferTypeSchema>;

export const sightglassOfferScopeSchema = z
  .enum(["sitewide", "collection", "product"])
  .describe("SightglassOfferScope");
export type SightglassOfferScope = z.infer<typeof sightglassOfferScopeSchema>;

export const sightglassOfferAudienceSchema = z
  .enum(["all", "first_order", "email_signup", "sms_signup", "subscribers"])
  .describe("SightglassOfferAudience");
export type SightglassOfferAudience = z.infer<
  typeof sightglassOfferAudienceSchema
>;

export const sightglassOfferEventSchema = z
  .enum([
    "black_friday",
    "cyber_monday",
    "bfcm",
    "labor_day",
    "memorial_day",
    "july_4th",
    "prime_day",
    "mothers_day",
    "fathers_day",
    "valentines_day",
    "back_to_school",
    "end_of_summer",
    "friends_and_family",
    "holiday",
    "new_year",
    "anniversary",
    "halloween",
    "singles_day",
  ])
  .describe("SightglassOfferEvent: a sale event named in the copy");
export type SightglassOfferEvent = z.infer<typeof sightglassOfferEventSchema>;

export const sightglassOfferRegionSchema = z
  .enum(["banner", "hero", "popup", "body", "ad_copy", "ad_image"])
  .describe("SightglassOfferRegion: where the quote was seen");

/** Quinn's twelve page types. The first five are dedicated landing pages, the default everywhere pages are browsed. */
export const sightglassPageTypeSchema = z
  .enum([
    "offer_page",
    "advertorial",
    "listicle",
    "quiz",
    "lead_capture",
    "homepage",
    "pdp",
    "collection",
    "blog",
    "marketplace",
    "app_store",
    "social_profile",
  ])
  .describe("SightglassPageType");
export type SightglassPageType = z.infer<typeof sightglassPageTypeSchema>;

export const SIGHTGLASS_LANDING_PAGE_TYPES: readonly SightglassPageType[] = [
  "offer_page",
  "advertorial",
  "listicle",
  "quiz",
  "lead_capture",
];

export const SIGHTGLASS_PAGE_TYPE_LABELS: Record<SightglassPageType, string> = {
  offer_page: "Offer page",
  advertorial: "Advertorial",
  listicle: "Listicle",
  quiz: "Quiz",
  lead_capture: "Lead capture",
  homepage: "Homepage",
  pdp: "PDP",
  collection: "Collection",
  blog: "Blog",
  marketplace: "Marketplace",
  app_store: "App store",
  social_profile: "Social profile",
};

/** What grabs attention first. Quinn's ten, then six that stopped "bold claim" from becoming a catch-all. */
export const sightglassHookTypeSchema = z
  .enum([
    "question",
    "bold_claim",
    "problem_callout",
    "curiosity",
    "proof_number",
    "before_after",
    "first_person_story",
    "offer_first",
    "us_vs_them",
    "demo",
    "audience_callout",
    "contrarian",
    "customer_testimonial",
    "expert_or_celebrity",
    "new_or_launch",
    "product_showcase",
  ])
  .describe("SightglassHookType");
export type SightglassHookType = z.infer<typeof sightglassHookTypeSchema>;

export const SIGHTGLASS_HOOK_LABELS: Record<SightglassHookType, string> = {
  question: "Question",
  bold_claim: "Bold claim",
  problem_callout: "Problem callout",
  curiosity: "Curiosity",
  proof_number: "Proof number",
  before_after: "Before/after",
  first_person_story: "First-person story",
  offer_first: "Offer first",
  us_vs_them: "Us vs them",
  demo: "Demo",
  audience_callout: "Audience callout",
  contrarian: "Contrarian",
  customer_testimonial: "Customer testimonial",
  expert_or_celebrity: "Expert or celebrity",
  new_or_launch: "New or launch",
  product_showcase: "Product showcase",
};

/** Quinn's offer list for filters and tags; standing store terms count here even though they never become campaigns. */
export const sightglassOfferTagSchema = z
  .enum([
    "discount",
    "bundle",
    "subscription",
    "trial_or_sample",
    "gift_with_purchase",
    "financing",
    "free_shipping",
    "giveaway",
  ])
  .describe("SightglassOfferTag");
export type SightglassOfferTag = z.infer<typeof sightglassOfferTagSchema>;

export const SIGHTGLASS_OFFER_TAG_LABELS: Record<SightglassOfferTag, string> = {
  discount: "Discount",
  bundle: "Bundle",
  subscription: "Subscription",
  trial_or_sample: "Trial or sample",
  gift_with_purchase: "Gift with purchase",
  financing: "Financing",
  free_shipping: "Free shipping",
  giveaway: "Giveaway",
};

export const sightglassFunnelStageSchema = z
  .enum(["cold", "warm", "retargeting"])
  .describe("SightglassFunnelStage");
export type SightglassFunnelStage = z.infer<typeof sightglassFunnelStageSchema>;

export const SIGHTGLASS_FUNNEL_STAGE_LABELS: Record<
  SightglassFunnelStage,
  string
> = { cold: "Cold", warm: "Warm", retargeting: "Retargeting" };

export const sightglassOfferReadingSchema = z
  .object({
    mechanic: sightglassOfferMechanicSchema,
    depthPercent: z
      .number()
      .nullable()
      .describe("Percent off as a number, e.g. 25; null if not stated"),
    amountOff: z
      .number()
      .nullable()
      .describe("Money off as a number, e.g. 50; null if not stated"),
    threshold: z
      .number()
      .nullable()
      .describe("Minimum spend to qualify, e.g. 75; null if none"),
    subject: z
      .string()
      .nullable()
      .describe(
        "What the offer applies to or includes, in a few words, e.g. 'the Cookware Set', 'a free You Solid'",
      ),
    scope: sightglassOfferScopeSchema,
    event: sightglassOfferEventSchema.nullable(),
    code: z.string().nullable(),
    audience: sightglassOfferAudienceSchema,
    quote: z
      .string()
      .describe("The exact words that state the offer, under 15 words"),
    region: sightglassOfferRegionSchema,
    standing: z
      .boolean()
      .describe(
        "True for always-on store terms rather than a promotion: free shipping over a threshold, free returns, subscribe-and-save defaults, loyalty points",
      ),
    confidence: z.number().describe("0 to 1"),
  })
  .describe("SightglassOfferReading");
export type SightglassOfferReading = z.infer<
  typeof sightglassOfferReadingSchema
>;

/** The pitch: which desire or pain point the ad or page leans on, and for whom. One per ad or page; offers sit on top of it. */
export const sightglassAngleTypeSchema = z
  .enum([
    "problem_solution",
    "mechanism",
    "proof",
    "comparison",
    "identity",
    "founder_story",
    "objection_handling",
    "urgency",
    "education",
    "transformation",
    "price_or_value",
    "quality_or_craft",
    "clean_or_non_toxic",
    "convenience",
    "gifting",
    "sustainability",
  ])
  .describe("SightglassAngleType");
export type SightglassAngleType = z.infer<typeof sightglassAngleTypeSchema>;

/** Who the angle speaks to, in the same shape and meaning as a Brand Studio persona, so the two can be compared and filtered alike. */
export const sightglassAnglePersonaSchema = z
  .object({
    nickname: z
      .string()
      .describe(
        "Segment label, two to four words, e.g. 'The weeknight cook'; never a person's name",
      ),
    headline: z
      .string()
      .describe("Situation plus problem in the buyer's own words, one line"),
    ageGte: z
      .number()
      .int()
      .nullable()
      .describe(
        "Youngest age the ad speaks to, estimated from the people, product and tone; null only with no signal at all",
      ),
    ageLt: z
      .number()
      .int()
      .nullable()
      .describe(
        "Exclusive upper age: 35 to 54 is ageGte 35, ageLt 55; null for open-ended",
      ),
    awareness: projectPersonaAwarenessSchema
      .nullable()
      .describe(
        "Where the buyer starts, judged from what the ad assumes they know",
      ),
  })
  .describe("SightglassAnglePersona");
export type SightglassAnglePersona = z.infer<
  typeof sightglassAnglePersonaSchema
>;

/** Age bands for filtering, by the midpoint of a persona's range. */
export const sightglassAgeBandSchema = z
  .enum(["18_24", "25_34", "35_44", "45_54", "55_plus", "any"])
  .describe("SightglassAgeBand");
export type SightglassAgeBand = z.infer<typeof sightglassAgeBandSchema>;

export const SIGHTGLASS_AGE_BAND_LABELS: Record<SightglassAgeBand, string> = {
  "18_24": "18 to 24",
  "25_34": "25 to 34",
  "35_44": "35 to 44",
  "45_54": "45 to 54",
  "55_plus": "55+",
  any: "Any age",
};

export function getPersonaAgeBand({
  ageGte,
  ageLt,
}: {
  ageGte: number | null;
  ageLt: number | null;
}): SightglassAgeBand {
  if (ageGte === null && ageLt === null) {
    return "any";
  }
  const low = ageGte ?? 18;
  const high = ageLt === null ? Math.max(low + 10, 65) : ageLt - 1;
  const middle = (low + high) / 2;
  if (middle < 25) {
    return "18_24";
  }
  if (middle < 35) {
    return "25_34";
  }
  if (middle < 45) {
    return "35_44";
  }
  if (middle < 55) {
    return "45_54";
  }
  return "55_plus";
}

export const sightglassAngleReadingSchema = z
  .object({
    type: sightglassAngleTypeSchema,
    name: z
      .string()
      .describe(
        "The specific idea in 2 to 6 words, e.g. 'Non-toxic nonstick coating'",
      ),
    hook: z
      .string()
      .describe("The exact opening line or headline claim, under 15 words"),
    hookType: sightglassHookTypeSchema,
    persona: sightglassAnglePersonaSchema,
    product: z
      .string()
      .nullable()
      .describe("What is being sold, in a few words; null for the whole brand"),
    confidence: z
      .number()
      .describe("0 to 1: how sure the hook and angle types are"),
  })
  .describe("SightglassAngleReading");
export type SightglassAngleReading = z.infer<
  typeof sightglassAngleReadingSchema
>;

export const sightglassPageReadingSchema = z
  .object({
    pageType: sightglassPageTypeSchema,
    summary: z.string().describe("One sentence on what the page sells"),
    angle: sightglassAngleReadingSchema.nullable(),
    offers: z.array(sightglassOfferReadingSchema),
  })
  .describe("SightglassPageReading");
export type SightglassPageReading = z.infer<typeof sightglassPageReadingSchema>;

export const sightglassCreativeReadingSchema = z
  .object({
    summary: z.string().describe("One sentence on what the ad sells"),
    angle: sightglassAngleReadingSchema.nullable(),
    offers: z.array(sightglassOfferReadingSchema),
    language: z
      .string()
      .nullable()
      .describe("ISO 639-1 code of the ad copy, e.g. 'en'; null if no words"),
  })
  .describe("SightglassCreativeReading");
export type SightglassCreativeReading = z.infer<
  typeof sightglassCreativeReadingSchema
>;

export function getOfferType(
  mechanic: SightglassOfferMechanic,
): SightglassOfferType {
  return exhaustiveSwitch({ type: mechanic })({
    percent_off: () => "discount" as const,
    money_off: () => "discount" as const,
    sitewide_sale: () => "sale" as const,
    buy_m_get_n: () => "bundle" as const,
    tiered_volume: () => "bundle" as const,
    bundle_price: () => "bundle" as const,
    free_gift: () => "free_gift" as const,
    free_shipping: () => "free_shipping" as const,
    giveaway: () => "giveaway" as const,
    subscription: () => "subscription" as const,
    trial_or_sample: () => "trial" as const,
    financing: () => "financing" as const,
  });
}

export function getOfferTag(
  mechanic: SightglassOfferMechanic,
): SightglassOfferTag {
  return exhaustiveSwitch({ type: mechanic })({
    percent_off: () => "discount" as const,
    money_off: () => "discount" as const,
    sitewide_sale: () => "discount" as const,
    buy_m_get_n: () => "bundle" as const,
    tiered_volume: () => "bundle" as const,
    bundle_price: () => "bundle" as const,
    free_gift: () => "gift_with_purchase" as const,
    free_shipping: () => "free_shipping" as const,
    giveaway: () => "giveaway" as const,
    subscription: () => "subscription" as const,
    trial_or_sample: () => "trial_or_sample" as const,
    financing: () => "financing" as const,
  });
}

/** Offer tags on one ad or page, standing terms included, low-confidence readings dropped. */
export function getOfferTags(
  offers: SightglassOfferReading[],
): SightglassOfferTag[] {
  const tags = offers
    .filter((offer) => offer.confidence >= SIGHTGLASS_TAG_MIN_CONFIDENCE)
    .map((offer) => getOfferTag(offer.mechanic));
  return [...new Set(tags)];
}

const COLD_PAGE_TYPES: ReadonlySet<SightglassPageType> = new Set([
  "advertorial",
  "listicle",
  "quiz",
  "blog",
]);

/** First-pass rules, checked in the same 100-ad round as the tags: education-first pages or copy are cold, a deal on a product page is retargeting. */
export function getFunnelStage({
  pageType,
  ctaType,
  awareness,
  offerTags,
}: {
  pageType: SightglassPageType | null;
  ctaType: string | null;
  awareness: string | null;
  offerTags: SightglassOfferTag[];
}): SightglassFunnelStage {
  const learnMore = (ctaType ?? "").toUpperCase() === "LEARN_MORE";
  if (
    (pageType && COLD_PAGE_TYPES.has(pageType)) ||
    learnMore ||
    awareness === "unaware" ||
    awareness === "problem"
  ) {
    return "cold";
  }
  if (offerTags.includes("discount") && pageType === "pdp") {
    return "retargeting";
  }
  return "warm";
}

export const SIGHTGLASS_OFFER_TYPE_LABELS: Record<SightglassOfferType, string> =
  {
    discount: "Discount",
    sale: "Sale",
    bundle: "Bundle",
    free_gift: "Free gift",
    free_shipping: "Free shipping",
    giveaway: "Giveaway",
    subscription: "Subscription",
    trial: "Trial",
    financing: "Financing",
  };

export const SIGHTGLASS_ANGLE_TYPE_LABELS: Record<SightglassAngleType, string> =
  {
    problem_solution: "Problem/solution",
    mechanism: "Mechanism",
    proof: "Proof",
    comparison: "Comparison",
    identity: "Identity",
    founder_story: "Founder story",
    objection_handling: "Objection handling",
    urgency: "Urgency",
    education: "Education",
    transformation: "Transformation",
    price_or_value: "Price or value",
    quality_or_craft: "Quality or craft",
    clean_or_non_toxic: "Clean or non-toxic",
    convenience: "Convenience",
    gifting: "Gifting",
    sustainability: "Sustainability",
  };

export const SIGHTGLASS_OFFER_EVENT_LABELS: Record<
  SightglassOfferEvent,
  string
> = {
  black_friday: "Black Friday",
  cyber_monday: "Cyber Monday",
  bfcm: "Black Friday Cyber Monday",
  labor_day: "Labor Day",
  memorial_day: "Memorial Day",
  july_4th: "the 4th of July",
  prime_day: "Prime Day",
  mothers_day: "Mother's Day",
  fathers_day: "Father's Day",
  valentines_day: "Valentine's Day",
  back_to_school: "Back to School",
  end_of_summer: "the End of Summer Sale",
  friends_and_family: "Friends & Family",
  holiday: "the holidays",
  new_year: "the New Year",
  anniversary: "an anniversary sale",
  halloween: "Halloween",
  singles_day: "Singles' Day",
};

/** The most brands one calendar request may name; each is a lookup and a slice of the sightings query. */
export const SIGHTGLASS_CALENDAR_MAX_COMPANIES = 50;

/** "The Cookware Set!" and "cookware set" name one thing; "a free You Solid" and "You Solid" too. */
function normalizeOfferSubject(subject: string | null): string {
  const normalized = (subject ?? "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^(?:(?:a|an|the|free) )+/, "");
  return normalized === "" ? "any" : normalized;
}

/** Identity of an offer within a company: what it is, not how deep it goes. Depth, code and threshold are tracked values, so a 5% → 10% change stays one campaign. */
export function getOfferSlot(offer: SightglassOfferReading): string {
  // "Up to 61% off sitewide" and "61% off everything" are one sale; the model files it either way.
  const sitewidePercent =
    offer.mechanic === "sitewide_sale" ||
    (offer.mechanic === "percent_off" && offer.scope === "sitewide");
  if (sitewidePercent) {
    // A sitewide sale is sitewide however the model scoped it.
    return `sitewide_percent:sitewide:${offer.event ?? "none"}`;
  }
  const slot = `${offer.mechanic}:${offer.scope}:${offer.event ?? "none"}`;
  // Deals on two different products in one event are two campaigns.
  return offer.scope === "sitewide"
    ? slot
    : `${slot}:${normalizeOfferSubject(offer.subject)}`;
}

function money(amount: number): string {
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
}

/** Title-case event name for labels, e.g. "Labor Day", "Friends & Family". */
export function getOfferEventTitle(
  event: SightglassOfferEvent | null,
): string | null {
  if (!event) {
    return null;
  }
  const label = SIGHTGLASS_OFFER_EVENT_LABELS[event].replace(/^(the|an) /, "");
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function describeOfferHead(offer: SightglassOfferReading): string {
  const what =
    offer.subject ??
    (offer.scope === "sitewide" ? "everything" : "select items");
  return exhaustiveSwitch({ type: offer.mechanic })({
    percent_off: () => {
      return offer.depthPercent === null
        ? `A discount on ${what}`
        : `${offer.depthPercent}% off ${what}`;
    },
    money_off: () => {
      return offer.amountOff === null
        ? `Money off ${what}`
        : `${money(offer.amountOff)} off ${what}`;
    },
    sitewide_sale: () => {
      return offer.depthPercent === null
        ? "A sitewide sale"
        : `Up to ${offer.depthPercent}% off sitewide`;
    },
    buy_m_get_n: () => `Buy more, get more on ${what}`,
    tiered_volume: () => `Buy more, save more on ${what}`,
    bundle_price: () => {
      return offer.subject === null
        ? "Bundle pricing"
        : `Bundle pricing on ${offer.subject}`;
    },
    free_gift: () => {
      return offer.subject === null
        ? "A free gift"
        : `Free ${offer.subject.replace(/^(a |an |free )/i, "")}`;
    },
    free_shipping: () => "Free shipping",
    giveaway: () => {
      return offer.subject === null
        ? "A giveaway"
        : `A giveaway for ${offer.subject}`;
    },
    subscription: () => {
      return offer.depthPercent === null
        ? "Subscribe and save"
        : `Subscribe and save ${offer.depthPercent}%`;
    },
    trial_or_sample: () => {
      return offer.subject === null
        ? "A trial or sample"
        : `Try ${offer.subject}`;
    },
    financing: () => "Pay over time",
  });
}

/** The short form a timeline bar can hold: the deal itself, led by the event when there is one. */
export function labelOffer(offer: SightglassOfferReading): string {
  const head = describeOfferHead(offer);
  const event = getOfferEventTitle(offer.event);
  return event ? `${event}: ${head}` : head;
}

/** Plain-English description built from the fields only, so it can never claim more than the reading says. */
export function describeOffer(offer: SightglassOfferReading): string {
  const parts = [describeOfferHead(offer)];
  if (offer.threshold !== null) {
    parts.push(`on orders over ${money(offer.threshold)}`);
  }
  if (offer.audience === "first_order") {
    parts.push("on your first order");
  } else if (offer.audience === "email_signup") {
    parts.push("when you sign up for email");
  } else if (offer.audience === "sms_signup") {
    parts.push("when you sign up for texts");
  } else if (offer.audience === "subscribers") {
    parts.push("for subscribers");
  }
  if (offer.event !== null) {
    parts.push(`for ${SIGHTGLASS_OFFER_EVENT_LABELS[offer.event]}`);
  }
  if (offer.code !== null) {
    parts.push(`with code ${offer.code}`);
  }
  return parts.join(" ");
}

/** One place an offer was seen: an ad (live between its Meta start and stop) or a page capture. */
export interface SightglassOfferSighting {
  companyId: string;
  source: "ad" | "page";
  /** Ad id or snapshot id. */
  sourceId: string;
  sitePageId: string | null;
  creativeId: string | null;
  startAt: string;
  endAt: string;
  /** An active ad, or a page whose newest capture still shows this offer. */
  live: boolean;
  offer: SightglassOfferReading;
}

/** One value the offer was seen at (a depth or an amount), with where and when. Values can overlap: two pages may show 58% and 61% the same week. */
export interface SightglassCampaignStep {
  from: string;
  to: string;
  depthPercent: number | null;
  amountOff: number | null;
  live: boolean;
  adIds: string[];
  sitePageIds: string[];
}

export interface SightglassGroupedCampaign {
  key: string;
  companyId: string;
  slot: string;
  offer: SightglassOfferReading;
  startAt: string;
  endAt: string;
  live: boolean;
  adIds: string[];
  sitePageIds: string[];
  creativeIds: string[];
  /** One entry per value seen, oldest first. */
  steps: SightglassCampaignStep[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

function valueKey(offer: SightglassOfferReading): string {
  return `${offer.depthPercent ?? ""}:${offer.amountOff ?? ""}`;
}

function uniqueIds(values: (string | null)[]): string[] {
  return [
    ...new Set(values.filter((value): value is string => value !== null)),
  ];
}

function buildSteps(
  byStart: SightglassOfferSighting[],
): SightglassCampaignStep[] {
  // A sighting that states no number ("sitewide sale") is only its own value when no sighting states one.
  const valued = byStart.filter((sighting) => {
    return (
      sighting.offer.depthPercent !== null || sighting.offer.amountOff !== null
    );
  });
  const source = valued.length > 0 ? valued : byStart;
  const byValue = new Map<string, SightglassOfferSighting[]>();
  for (const sighting of source) {
    const key = valueKey(sighting.offer);
    const list = byValue.get(key) ?? [];
    list.push(sighting);
    byValue.set(key, list);
  }
  const steps: SightglassCampaignStep[] = [];
  for (const list of byValue.values()) {
    const first = list[0];
    if (!first) {
      continue;
    }
    steps.push({
      from: first.startAt,
      to: list.reduce(
        (max, sighting) => (sighting.endAt > max ? sighting.endAt : max),
        first.endAt,
      ),
      depthPercent: first.offer.depthPercent,
      amountOff: first.offer.amountOff,
      live: list.some((sighting) => sighting.live),
      adIds: uniqueIds(
        list.map((sighting) => {
          return sighting.source === "ad" ? sighting.sourceId : null;
        }),
      ),
      sitePageIds: uniqueIds(list.map((sighting) => sighting.sitePageId)),
    });
  }
  return steps.sort((left, right) => left.from.localeCompare(right.from));
}

function buildCampaign(
  group: SightglassOfferSighting[],
): SightglassGroupedCampaign {
  const byStart = [...group].sort((left, right) => {
    return left.startAt.localeCompare(right.startAt);
  });
  const steps = buildSteps(byStart);
  const newest = byStart[byStart.length - 1];
  if (!newest || steps.length === 0) {
    throw new RangeError("a campaign needs at least one sighting");
  }
  const reach = (step: SightglassCampaignStep) => {
    return step.adIds.length + step.sitePageIds.length;
  };
  // While live, the value most ads and pages carry now; once ended, the value it ended on.
  const headline = steps.reduce((best, step) => {
    if (step.live !== best.live) {
      return step.live ? step : best;
    }
    if (!step.live && step.to !== best.to) {
      return step.to > best.to ? step : best;
    }
    return reach(step) > reach(best) ||
      (reach(step) === reach(best) && step.from > best.from)
      ? step
      : best;
  });
  const representative =
    byStart
      .filter((sighting) => {
        return (
          sighting.offer.depthPercent === headline.depthPercent &&
          sighting.offer.amountOff === headline.amountOff
        );
      })
      .reduce<SightglassOfferSighting | null>((best, sighting) => {
        return best === null ||
          sighting.offer.confidence > best.offer.confidence
          ? sighting
          : best;
      }, null) ?? newest;
  const unique = uniqueIds;
  const startAt = byStart.reduce(
    (min, sighting) => (sighting.startAt < min ? sighting.startAt : min),
    newest.startAt,
  );
  const endAt = byStart.reduce(
    (max, sighting) => (sighting.endAt > max ? sighting.endAt : max),
    newest.endAt,
  );
  return {
    key: `${newest.companyId}:${getOfferSlot(newest.offer)}:${startAt.slice(0, 10)}`,
    companyId: newest.companyId,
    slot: getOfferSlot(newest.offer),
    offer: representative.offer,
    startAt,
    endAt,
    live: group.some((sighting) => sighting.live),
    adIds: unique(
      group.map((sighting) => {
        return sighting.source === "ad" ? sighting.sourceId : null;
      }),
    ),
    sitePageIds: unique(group.map((sighting) => sighting.sitePageId)),
    creativeIds: unique(group.map((sighting) => sighting.creativeId)),
    steps,
  };
}

/** The model occasionally reads "Save up to $80" as 80% off; a percent that its own quote never states is dropped. */
export function withCheckedDepth(
  offer: SightglassOfferReading,
): SightglassOfferReading {
  if (
    offer.depthPercent !== null &&
    offer.quote.includes("$") &&
    !offer.quote.includes("%")
  ) {
    return { ...offer, depthPercent: null };
  }
  return offer;
}

/** Offers → campaigns: same company and slot, no gap longer than `gapDays`. Standing store terms never become campaigns. Pure, so the calendar can be recomputed for any window. */
export function groupOfferSightings(
  sightings: SightglassOfferSighting[],
  { gapDays = SIGHTGLASS_CAMPAIGN_GAP_DAYS }: { gapDays?: number } = {},
): SightglassGroupedCampaign[] {
  const bySlot = new Map<string, SightglassOfferSighting[]>();
  for (const sighting of sightings) {
    if (
      sighting.offer.confidence < SIGHTGLASS_OFFER_MIN_CONFIDENCE ||
      sighting.offer.standing
    ) {
      continue;
    }
    const checked = { ...sighting, offer: withCheckedDepth(sighting.offer) };
    const key = `${checked.companyId}:${getOfferSlot(checked.offer)}`;
    const list = bySlot.get(key) ?? [];
    list.push(checked);
    bySlot.set(key, list);
  }
  const campaigns: SightglassGroupedCampaign[] = [];
  for (const list of bySlot.values()) {
    const sorted = [...list].sort((left, right) => {
      return left.startAt.localeCompare(right.startAt);
    });
    let current: SightglassOfferSighting[] = [];
    let currentEnd = "";
    for (const sighting of sorted) {
      const gap = Date.parse(sighting.startAt) - Date.parse(currentEnd);
      if (current.length > 0 && gap > gapDays * DAY_MS) {
        campaigns.push(buildCampaign(current));
        current = [];
      }
      current.push(sighting);
      currentEnd =
        current.length === 1 || sighting.endAt > currentEnd
          ? sighting.endAt
          : currentEnd;
    }
    if (current.length > 0) {
      campaigns.push(buildCampaign(current));
    }
  }
  return campaigns.sort((left, right) => {
    return right.startAt.localeCompare(left.startAt);
  });
}

/** One place an angle was seen: an ad (live between its Meta start and stop) or a page capture. */
export interface SightglassAngleSighting {
  companyId: string;
  source: "ad" | "page";
  sourceId: string;
  sitePageId: string | null;
  creativeId: string | null;
  startAt: string;
  endAt: string;
  live: boolean;
  angle: SightglassAngleReading;
}

export interface SightglassGroupedAngle {
  key: string;
  companyId: string;
  type: SightglassAngleType;
  /** The spelling most sightings used. */
  name: string;
  /** The newest hook, so the leaderboard shows the current pitch. */
  hook: string;
  /** The persona most sightings named, with that persona's own age range and headline. */
  persona: SightglassAnglePersona;
  product: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  live: boolean;
  adIds: string[];
  liveAdIds: string[];
  sitePageIds: string[];
  creativeIds: string[];
}

/** Angle identity within a company: its name with case, punctuation and filler removed. The type is not part of it, because the model can file the same idea under two types. */
export function getAngleSlot(angle: SightglassAngleReading): string {
  const name = angle.name
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\b(the|a|an|and|for|of|your)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return name;
}

function mostCommon<Value extends string>(values: Value[]): Value | null {
  const counts = new Map<Value, number>();
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  let best: Value | null = null;
  let bestCount = 0;
  for (const [value, count] of counts) {
    if (count > bestCount) {
      best = value;
      bestCount = count;
    }
  }
  return best;
}

function representativePersona(
  group: SightglassAngleSighting[],
): SightglassAnglePersona {
  const nickname = mostCommon(
    group.map((sighting) => sighting.angle.persona.nickname),
  );
  const named = [...group]
    .reverse()
    .find((sighting) => sighting.angle.persona.nickname === nickname);
  const base = (named ?? group[group.length - 1])?.angle.persona;
  if (!base) {
    throw new RangeError("an angle needs at least one sighting");
  }
  const awarenessValues = group
    .map((sighting) => sighting.angle.persona.awareness)
    .filter((value) => value !== null);
  return { ...base, awareness: mostCommon(awarenessValues) ?? base.awareness };
}

/** Readings → angles: same company and slot, across all time. Unlike offers there is no gap rule, since a brand keeps coming back to its angles. */
export function groupAngleSightings(
  sightings: SightglassAngleSighting[],
): SightglassGroupedAngle[] {
  const bySlot = new Map<string, SightglassAngleSighting[]>();
  for (const sighting of sightings) {
    const key = `${sighting.companyId}:${getAngleSlot(sighting.angle)}`;
    const list = bySlot.get(key) ?? [];
    list.push(sighting);
    bySlot.set(key, list);
  }
  const angles: SightglassGroupedAngle[] = [];
  for (const [key, group] of bySlot) {
    const byStart = [...group].sort((left, right) => {
      return left.startAt.localeCompare(right.startAt);
    });
    const first = byStart[0];
    const newest = byStart[byStart.length - 1];
    if (!first || !newest) {
      continue;
    }
    const unique = (values: (string | null)[]) => [
      ...new Set(values.filter((value): value is string => value !== null)),
    ];
    angles.push({
      key,
      companyId: newest.companyId,
      type:
        mostCommon(group.map((sighting) => sighting.angle.type)) ??
        newest.angle.type,
      name:
        mostCommon(group.map((sighting) => sighting.angle.name)) ??
        newest.angle.name,
      hook: newest.angle.hook,
      persona: representativePersona(group),
      product: mostCommon(
        unique(group.map((sighting) => sighting.angle.product)),
      ),
      firstSeenAt: first.startAt,
      lastSeenAt: byStart.reduce(
        (max, sighting) => (sighting.endAt > max ? sighting.endAt : max),
        newest.endAt,
      ),
      live: group.some((sighting) => sighting.live),
      adIds: unique(
        group.map((sighting) => {
          return sighting.source === "ad" ? sighting.sourceId : null;
        }),
      ),
      liveAdIds: unique(
        group.map((sighting) => {
          return sighting.source === "ad" && sighting.live
            ? sighting.sourceId
            : null;
        }),
      ),
      sitePageIds: unique(group.map((sighting) => sighting.sitePageId)),
      creativeIds: unique(group.map((sighting) => sighting.creativeId)),
    });
  }
  return angles;
}
